// src/components/expenses/ExpenseList.jsx
import React, { useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useExpenses } from '../../hooks/useExpenses';
import { useAuth } from '../../context/AuthContext';
import { usePlanLimits } from '../../hooks/usePlanLimits';
import LoadingSpinner from '../common/LoadingSpinner';
import ConfirmDialog from '../common/ConfirmDialog';
import ExpenseFilters from './ExpenseFilters';
import ExportButton from '../common/ExportButton';
import PlanLimitBadge from '../common/PlanLimitBadge';
import { exportExpenses } from '../../utils/exportUtils';
import { formatCurrency, formatDate } from '../../utils/formatters';
import { expenseApi } from '../../api/expenseApi';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import {
    PlusIcon, PencilSquareIcon, TrashIcon,
    CheckCircleIcon, DocumentTextIcon
} from '@heroicons/react/24/outline';

const CATEGORY_ICONS = {
    MATERIALS: '🧱', EQUIPMENT: '🚜', LABOR: '👷', FUEL: '⛽',
    SUBCONTRACT: '🤝', TRANSPORT: '🚛', PERMITS: '📋', ADMIN: '📁', OTHER: '📦',
};
const CATEGORY_LABELS = {
    MATERIALS: 'Materiali', EQUIPMENT: 'Attrezzature', LABOR: 'Manodopera',
    FUEL: 'Carburante', SUBCONTRACT: 'Subappalto', TRANSPORT: 'Trasporto',
    PERMITS: 'Permessi', ADMIN: 'Amministrazione', OTHER: 'Altro',
};

const DEFAULT_FILTERS = {
    search: '', category: '', projectId: '', validated: '',
    startDate: '', endDate: '', minAmount: '', maxAmount: '',
    supplier: '', paymentMethod: '', sortBy: 'createdAt',
    page: 1, limit: 15,
};

const ExpenseList = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { user, organization } = useAuth();
    const currency = organization?.currency || 'GNF';
    const { expenses: expenseLimits } = usePlanLimits();

    const [filters, setFilters] = useState(DEFAULT_FILTERS);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [validating, setValidating] = useState(null);

    const { expenses, loading, error, pagination, deleteExpense, refreshExpenses } = useExpenses(filters);

    const canValidate = ['SUPER_ADMIN', 'SITE_MANAGER', 'ACCOUNTANT'].includes(user?.role);
    const canDelete = ['SUPER_ADMIN'].includes(user?.role);
    const canEdit = ['SUPER_ADMIN', 'SITE_MANAGER', 'ENGINEER', 'ACCOUNTANT'].includes(user?.role);

    const handleValidate = async (id) => {
        setValidating(id);
        try {
            await expenseApi.validate(id);
            toast.success('Spesa validata!');
            refreshExpenses?.();
        } catch {
            toast.error('Errore durante la validazione');
        } finally {
            setValidating(null);
        }
    };

    const handleDelete = async () => {
        if (!deleteTarget) return;
        await deleteExpense(deleteTarget);
        setDeleteTarget(null);
    };

    const handleReset = useCallback(() => setFilters(DEFAULT_FILTERS), []);

    const totalAmount = expenses.reduce((s, e) => s + (e.totalAmount || 0), 0);
    const validatedAmount = expenses.filter(e => e.validated).reduce((s, e) => s + (e.totalAmount || 0), 0);

    if (loading && expenses.length === 0) return <LoadingSpinner />;

    return (
        <div className="space-y-5">

            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <div className="flex items-center gap-3 flex-wrap">
                        <h1 className="text-2xl font-bold text-gray-900">{t('expenses.title')}</h1>
                        {/* ── Badge limite piano ── */}
                        <PlanLimitBadge
                            icon="💰"
                            label="Spese"
                            used={expenseLimits.used}
                            max={expenseLimits.max}
                            warnAt={80}
                        />
                    </div>
                    <p className="text-gray-500 text-sm mt-0.5">{pagination?.total || expenses.length} spese totali</p>
                </div>
                <div className="flex items-center gap-2">
                    <ExportButton
                        label="Esporta"
                        onExportCSV={() => exportExpenses(expenses, currency, 'csv')}
                        onExportXLS={() => exportExpenses(expenses, currency, 'xlsx')}
                    />
                    {canEdit && (
                        <Link to="/expenses/new"
                            className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2.5 rounded-xl hover:bg-blue-700 transition font-medium text-sm">
                            <PlusIcon className="w-4 h-4" />
                            Nuova Spesa
                        </Link>
                    )}
                </div>
            </div>

            {/* Filtri */}
            <ExpenseFilters
                filters={filters}
                onChange={setFilters}
                onReset={handleReset}
                totalResults={pagination?.total}
            />

            {/* Sommario */}
            {expenses.length > 0 && (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    {[
                        { label: 'Mostrate', value: expenses.length, sub: `di ${pagination?.total || expenses.length}` },
                        { label: 'Totale filtrato', value: formatCurrency(totalAmount, currency), sub: null },
                        { label: 'Validate', value: expenses.filter(e => e.validated).length, sub: `${((expenses.filter(e => e.validated).length / expenses.length) * 100).toFixed(0)}%` },
                        { label: 'Importo validato', value: formatCurrency(validatedAmount, currency), sub: null },
                    ].map((item, i) => (
                        <div key={i} className="bg-white rounded-xl border p-3.5">
                            <p className="text-xs text-gray-400 font-medium">{item.label}</p>
                            <p className="text-lg font-bold text-gray-900 mt-0.5">{item.value}</p>
                            {item.sub && <p className="text-xs text-gray-400">{item.sub}</p>}
                        </div>
                    ))}
                </div>
            )}

            {/* Errore */}
            {error && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-xl text-red-600 text-sm">⚠️ {error}</div>
            )}

            {/* Lista */}
            {expenses.length === 0 ? (
                <div className="bg-white rounded-2xl border p-16 text-center">
                    <div className="text-5xl mb-4">💰</div>
                    <h3 className="text-lg font-semibold text-gray-900">Nessuna spesa trovata</h3>
                    <p className="text-gray-500 mt-1 mb-6 text-sm">
                        {Object.values(filters).some(v => v !== '' && v !== 1 && v !== 15)
                            ? 'Prova a modificare i filtri di ricerca'
                            : 'Registra la prima spesa del cantiere'
                        }
                    </p>
                    {canEdit && (
                        <Link to="/expenses/new" className="inline-flex items-center gap-2 bg-blue-600 text-white px-6 py-2.5 rounded-xl hover:bg-blue-700 transition font-medium">
                            <PlusIcon className="w-5 h-5" /> Nuova Spesa
                        </Link>
                    )}
                </div>
            ) : (
                <div className="bg-white rounded-2xl border overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead>
                                <tr className="border-b bg-gray-50">
                                    <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Spesa</th>
                                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Progetto</th>
                                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">{t('expenses.category')}</th>
                                    <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">{t('expenses.amount')}</th>
                                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Data</th>
                                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Stato</th>
                                    <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">{t('common.actions')}</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-50">
                                {expenses.map(expense => (
                                    <tr key={expense.id} className="hover:bg-gray-50 transition group">
                                        <td className="px-5 py-4">
                                            <div>
                                                <p className="text-sm font-semibold text-gray-900">{expense.reference}</p>
                                                <p className="text-xs text-gray-400 truncate max-w-xs mt-0.5">{expense.description}</p>
                                                {expense.supplierName && (
                                                    <p className="text-xs text-gray-400 mt-0.5">🏭 {expense.supplierName}</p>
                                                )}
                                            </div>
                                        </td>
                                        <td className="px-4 py-4">
                                            <p className="text-xs font-medium text-gray-700">{expense.project?.name || '—'}</p>
                                            <p className="text-xs text-gray-400 font-mono">{expense.project?.code}</p>
                                        </td>
                                        <td className="px-4 py-4">
                                            <span className="inline-flex items-center gap-1.5 text-xs bg-gray-100 text-gray-700 px-2.5 py-1 rounded-lg font-medium">
                                                {CATEGORY_ICONS[expense.category]}
                                                {CATEGORY_LABELS[expense.category] || expense.category}
                                            </span>
                                        </td>
                                        <td className="px-4 py-4 text-right">
                                            <p className="text-sm font-bold text-gray-900">{formatCurrency(expense.totalAmount, currency)}</p>
                                            {expense.taxRate > 0 && (
                                                <p className="text-xs text-gray-400 mt-0.5">IVA {expense.taxRate}%</p>
                                            )}
                                        </td>
                                        <td className="px-4 py-4">
                                            <p className="text-xs text-gray-600">{formatDate(expense.invoiceDate)}</p>
                                            {expense.paymentDate && (
                                                <p className="text-xs text-gray-400">Pagato: {formatDate(expense.paymentDate)}</p>
                                            )}
                                        </td>
                                        <td className="px-4 py-4">
                                            {expense.validated ? (
                                                <div>
                                                    <span className="inline-flex items-center gap-1 text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full font-medium">
                                                        <CheckCircleIcon className="w-3 h-3" /> Validata
                                                    </span>
                                                    {expense.validatedBy && (
                                                        <p className="text-xs text-gray-400 mt-0.5">
                                                            {expense.validatedBy.firstName} {expense.validatedBy.lastName}
                                                        </p>
                                                    )}
                                                </div>
                                            ) : (
                                                <span className="inline-flex items-center gap-1 text-xs bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded-full font-medium">
                                                    ⏳ In attesa
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-4 py-4">
                                            <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition">
                                                {expense.invoiceUrl && (
                                                    <a href={expense.invoiceUrl} target="_blank" rel="noreferrer"
                                                        className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition" title="Vedi fattura">
                                                        <DocumentTextIcon className="w-4 h-4" />
                                                    </a>
                                                )}
                                                {canEdit && (
                                                    <button onClick={() => navigate(`/expenses/${expense.id}/edit`)}
                                                        className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition">
                                                        <PencilSquareIcon className="w-4 h-4" />
                                                    </button>
                                                )}
                                                {canValidate && !expense.validated && (
                                                    <button
                                                        onClick={() => handleValidate(expense.id)}
                                                        disabled={validating === expense.id}
                                                        className="p-1.5 text-gray-400 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg transition disabled:opacity-50"
                                                        title="Valida spesa"
                                                    >
                                                        {validating === expense.id
                                                            ? <span className="w-4 h-4 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin inline-block" />
                                                            : <CheckCircleIcon className="w-4 h-4" />
                                                        }
                                                    </button>
                                                )}
                                                {canDelete && (
                                                    <button onClick={() => setDeleteTarget(expense.id)}
                                                        className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition">
                                                        <TrashIcon className="w-4 h-4" />
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* Paginazione */}
                    {pagination?.totalPages > 1 && (
                        <div className="px-5 py-3 border-t bg-gray-50 flex items-center justify-between">
                            <p className="text-xs text-gray-500">
                                Pagina {pagination.page} di {pagination.totalPages} · {pagination.total} totali
                            </p>
                            <div className="flex gap-2">
                                <button
                                    disabled={pagination.page <= 1}
                                    onClick={() => setFilters(f => ({ ...f, page: f.page - 1 }))}
                                    className="px-3 py-1.5 text-xs border border-gray-200 rounded-lg hover:bg-gray-100 disabled:opacity-40 transition font-medium"
                                >
                                    ← Precedente
                                </button>
                                <button
                                    disabled={pagination.page >= pagination.totalPages}
                                    onClick={() => setFilters(f => ({ ...f, page: f.page + 1 }))}
                                    className="px-3 py-1.5 text-xs border border-gray-200 rounded-lg hover:bg-gray-100 disabled:opacity-40 transition font-medium"
                                >
                                    Successiva →
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {deleteTarget && (
                <ConfirmDialog
                    title="Elimina Spesa"
                    message="Questa operazione è irreversibile. La spesa verrà rimossa e il budget del progetto aggiornato."
                    confirmText="Elimina"
                    type="danger"
                    onConfirm={handleDelete}
                    onCancel={() => setDeleteTarget(null)}
                />
            )}
        </div>
    );
};

export default ExpenseList;
